import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { useAppSnackbar } from '../../common/TableDesign';
import ModulePageHeader from '../../common/ModulePageHeader';
import { AccountPicker, CustomerPicker } from '../LedgerPickers';
import DocResultDialog from './DocResultDialog';
import StockLinesEditor from './StockLinesEditor';
import { fmtDate, getErrorMessage, itemLabelOf } from './InventoryShared';

const ISSUE_TYPES = ['SALE', 'CONSUMPTION', 'WRITE_OFF'].map((t) => ({ label: t.replace(/_/g, ' '), value: t }));

const emptyLine = () => ({ item_id: '', quantity: 1 });
const emptyForm = () => ({
  issue_type: 'CONSUMPTION',
  issue_date: new Date().toISOString().slice(0, 10),
  customer_id: '',
  consumption_account_id: '',
  ar_invoice_id: '',
  ar_invoice_number: '',
  lines: [emptyLine()],
});

// Goods issues: stock going out (verified against CreateGoodsIssueDto).
// CONSUMPTION issues need a consumption_account_id (DR instead of COGS).
// Write-only like receipts - the result dialog carries approve/post/reverse.
const IssuesTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();

  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  if (!can(RESOURCES.LEDGER.INVENTORY, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Goods Issues.</Alert>;
  }

  const set = (key, value) => setValues((p) => ({ ...p, [key]: value }));
  const idOf = (d) => d?.issue_id ?? d?.id;
  const numberOf = (d) => d?.issue_number || (idOf(d) ? `GIN-${idOf(d)}` : '');

  const submitCreate = async () => {
    if (values.issue_type === 'CONSUMPTION' && !values.consumption_account_id) {
      showSnackbar('Consumption account is required for CONSUMPTION issues', 'error');
      return;
    }
    const lines = values.lines.filter((l) => l.item_id || l.quantity);
    if (lines.length < 1) {
      showSnackbar('At least one line is required', 'error');
      return;
    }
    if (lines.some((l) => !l.item_id || !(Number(l.quantity) > 0))) {
      showSnackbar('Each line needs an item and a quantity greater than zero', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await ledgerAPI.createInvIssue({
        issue_type: values.issue_type,
        issue_date: values.issue_date,
        ...(values.customer_id ? { customer_id: Number(values.customer_id) } : {}),
        ...(values.consumption_account_id ? { consumption_account_id: Number(values.consumption_account_id) } : {}),
        ...(values.ar_invoice_id !== '' ? { ar_invoice_id: Number(values.ar_invoice_id) } : {}),
        ...(values.ar_invoice_number?.trim() ? { ar_invoice_number: values.ar_invoice_number.trim() } : {}),
        lines: lines.map((l) => ({ item_id: Number(l.item_id), quantity: Number(l.quantity) })),
      });
      const doc = res?.data?.data ?? res?.data ?? {};
      setResult({ ...doc, id: idOf(doc) ?? doc.issue_id });
      setShowCreate(false);
      setValues(emptyForm());
      showSnackbar(`Issue ${numberOf(doc)} created as DRAFT`, 'success');
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <ModulePageHeader
        title="Goods Issues"
        subtitle="Stock going out - sales, consumption and write-offs. Cost posts from FIFO layers."
        actions={canCreate ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => { setValues(emptyForm()); setShowCreate(true); }}>
            New Goods Issue
          </Button>,
        ] : null}
      />
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Issues are tracked by document number (returned on creation). SALE issues can link an AR invoice for
            margin reporting (Reports -&gt; Sales Margin).
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Goods Issue</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" select label="Issue Type" value={values.issue_type} onChange={(e) => set('issue_type', e.target.value)}>
                {ISSUE_TYPES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="date" label="Issue Date" value={values.issue_date}
                onChange={(e) => set('issue_date', e.target.value)} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomerPicker value={values.customer_id} onChange={(id) => set('customer_id', id)} />
            </Grid>
            {values.issue_type === 'CONSUMPTION' && (
              <Grid size={{ xs: 12, md: 6 }}>
                <AccountPicker label="Consumption account (DR instead of COGS)" value={values.consumption_account_id} onChange={(id) => set('consumption_account_id', id)} required />
              </Grid>
            )}
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="number" label="AR Invoice ID" value={values.ar_invoice_id}
                onChange={(e) => set('ar_invoice_id', e.target.value)} inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" label="AR Invoice Number" value={values.ar_invoice_number} onChange={(e) => set('ar_invoice_number', e.target.value)} />
            </Grid>
            <Grid size={12}>
              <StockLinesEditor lines={values.lines} setLines={(lines) => set('lines', lines)} showUnitCost={false} emptyLine={emptyLine} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocResultDialog
        open={!!result}
        onClose={() => setResult(null)}
        title="Goods issue"
        doc={result}
        setDoc={setResult}
        numberOf={numberOf}
        statusOf={(d) => d.status}
        api={{
          approve: (id) => ledgerAPI.approveInvIssue(id),
          post: (id) => ledgerAPI.postInvIssue(id),
          reverse: (id, d) => ledgerAPI.reverseInvIssue(id, d),
        }}
        canApprove={canApprove}
        renderSummary={(d) => (
          <>
            <Typography variant="body2" color="text.secondary">{String(d.issue_type || '').replace(/_/g, ' ')} - {fmtDate(d.issue_date)}</Typography>
            {(d.lines || []).map((l, i) => (
              <Typography key={l.line_id ?? i} variant="body2">
                {l.item ? itemLabelOf(l.item) : `Item ${l.item_id ?? ''}`} - qty {l.quantity}
              </Typography>
            ))}
          </>
        )}
      />

      {SnackbarComponent}
    </Box>
  );
};

export default IssuesTab;
